import { useState } from 'react'
import { Link } from 'react-router'
import { useCompanies, useCreateProject, useDirectory, useProjects } from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import { fromDateInputValue, formatDate } from '../lib/dates'
import {
  Button,
  Chip,
  EmptyState,
  ErrorNotice,
  Loading,
  PageHeader,
  ProjectStatusPill,
} from '../components/ui'

export function ProjectsPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const [companyId, setCompanyId] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [creating, setCreating] = useState(false)

  const { data: projects, isLoading, error } = useProjects({
    companyId: companyId || undefined,
    includeArchived,
  })
  const { data: companies } = useCompanies()

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Engagements and workstreams, grouped under the entity that owns them."
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setCreating((v) => !v)}>
              {creating ? 'Cancel' : 'New project'}
            </Button>
          ) : undefined
        }
      />

      {creating && isAdmin && <NewProjectForm onDone={() => setCreating(false)} />}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <select
          className="field max-w-xs"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          aria-label="Filter by company"
        >
          <option value="">All companies</option>
          {companies?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-slate-300 dark:border-slate-600"
          />
          Show archived
        </label>
      </div>

      {isLoading && <Loading />}
      {error && <ErrorNotice error={error} />}

      {projects && projects.length === 0 && (
        <EmptyState
          title="No projects yet"
          description={
            isAdmin
              ? 'Create a company first if you have not, then add a project to it.'
              : 'Once you are added to a project it will show up here.'
          }
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {projects?.map((project) => (
          <Link
            key={project.id}
            to={`/projects/${project.id}`}
            className="card flex flex-col p-4 transition-shadow hover:shadow-md"
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <Chip>{project.company.shortName ?? project.company.name}</Chip>
              <ProjectStatusPill status={project.status} />
            </div>

            <h2 className="text-base font-semibold tracking-tight">{project.name}</h2>

            {project.description && (
              <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                {project.description}
              </p>
            )}

            <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex gap-1">
                <dt>Open</dt>
                <dd className="font-medium tabular-nums text-slate-700 dark:text-slate-300">
                  {project.taskCounts.open}
                </dd>
              </div>
              {project.taskCounts.overdue > 0 && (
                <div className="flex gap-1 text-red-600 dark:text-red-400">
                  <dt>Overdue</dt>
                  <dd className="font-medium tabular-nums">{project.taskCounts.overdue}</dd>
                </div>
              )}
              {project.targetDate && (
                <div className="flex gap-1">
                  <dt>Target</dt>
                  <dd>{formatDate(project.targetDate)}</dd>
                </div>
              )}
            </dl>

            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              {project.lead ? `Lead: ${project.lead.name}` : 'No lead assigned'}
              {project.members.length > 0 && ` · ${project.members.length} on the team`}
            </p>
          </Link>
        ))}
      </div>
    </>
  )
}

function NewProjectForm({ onDone }: { onDone: () => void }) {
  const createProject = useCreateProject()
  const { data: companies } = useCompanies()
  const { data: people } = useDirectory()

  const [name, setName] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [leadUserId, setLeadUserId] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [description, setDescription] = useState('')

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!name.trim() || !companyId) return
    createProject.mutate(
      {
        name: name.trim(),
        companyId,
        leadUserId: leadUserId || null,
        targetDate: fromDateInputValue(targetDate),
        description: description.trim() || null,
      },
      { onSuccess: onDone },
    )
  }

  const noCompanies = companies?.length === 0

  return (
    <form onSubmit={handleSubmit} className="card mb-6 space-y-3 p-4">
      <h2 className="text-sm font-semibold">New project</h2>

      {noCompanies && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
          Add a company first. Every project belongs to one.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="p-name">
            Project name
          </label>
          <input
            id="p-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="label" htmlFor="p-company">
            Company
          </label>
          <select
            id="p-company"
            className="field"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
          >
            <option value="">Choose…</option>
            {companies?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="p-lead">
            Lead
          </label>
          <select
            id="p-lead"
            className="field"
            value={leadUserId}
            onChange={(e) => setLeadUserId(e.target.value)}
          >
            <option value="">No lead yet</option>
            {people?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            The lead can manage the project and its team.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="p-target">
            Target date
          </label>
          <input
            id="p-target"
            type="date"
            className="field"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="label" htmlFor="p-desc">
          Description
        </label>
        <textarea
          id="p-desc"
          className="field min-h-20"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this engagement, in a line or two?"
        />
      </div>

      {createProject.error && <ErrorNotice error={createProject.error} />}

      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          loading={createProject.isPending}
          disabled={!name.trim() || !companyId}
        >
          Create project
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
